import {
    AssetTableRowData,
    TokenRowData,
} from "~/modules/assets/components/AssetTable/types";
import { HOLDING_ADDRESS_COUNT_COLUMN } from "~/modules/assets/components/AssetTable/columns/Holders";
import { NetworkBadge } from "~/modules/assets/components/AssetTable/NetworkBadge";
import { TokenMessages } from "~/modules/assets/components/AssetTable/TokenMessages";
import {
    ColumnMetadata,
    DataGrid,
    DataGridRow,
    RowSizes,
    ColumnTypes,
} from "~/components/DataGrid";
import { Asset, AssetColumns } from "~/modules/assets/types";
import { useInMemoryAssetSort } from "~/modules/assets/components/AssetTable/useInMemoryAssetSort";
import { SortDirections } from "~/modules/filters/types";
import {
    BRIDGED_TOKEN_SUPPLY_TOKEN_COLUMN,
    CIRCULATING_SUPPLY_TOKEN_COLUMN,
    TOTAL_SUPPLY_TOKEN_COLUMN,
} from "../../components/AssetTable/columns/TokenSupply";
import { Link } from "~/components/Link";
import { getNetworkURL } from "~/routes";
import { AssetUserEvents } from "~/modules/analytics/events/assets";
import { TRAILING_30_DAY_ACTIVE_ADDRESSES_COUNT_COLUMN } from "../../components/AssetTable/columns/ActiveAddresses";
import { CONTRACT_ADDRESS_COLUMN } from "../../components/AssetTable/columns/ContractAddress";
import { TOKEN_STANDARD_COLUMN } from "../../components/AssetTable/columns/TokenStandard";
import { TRAILING_30_DAY_TRANSFER_COUNT_COLUMN } from "../../components/AssetTable/columns/TransferCount";
import { TRAILING_30_DAY_TRANSFER_VOLUME_COLUMN } from "../../components/AssetTable/columns/TransferVolume";
import { Badge, BadgeVariants } from "~/components/Badge";
import { Tooltip } from "~/components/Tooltip";
import { Emdash } from "~/components/Punctuation";
import { TOKENIZATION_TYPE_COLUMN } from "../../components/AssetTable/columns/TokenizationType";

// // // //

type TokenIssuanceType = "NATIVE" | "BRIDGE";

const getTokenIssuanceType = (
    token: TokenRowData,
): TokenIssuanceType | null => {
    const issuanceType = token.issuance_type ?? token.platform?.issuance_type;

    if (issuanceType === "NATIVE" || issuanceType === "BRIDGE") {
        return issuanceType;
    }

    return null;
};

/**
 * Renders a token issuance badge with contextual bridge tooltip.
 */
function TokenIssuanceTypeBadge(props: { token: TokenRowData }) {
    const { token } = props;
    const issuanceType = getTokenIssuanceType(token);

    if (!issuanceType) {
        return null;
    }

    const isBridged = issuanceType === "BRIDGE";
    const label = isBridged ? "Bridged" : "Native";
    const platformName = token.platform?.name || "Unknown platform";

    const badge = (
        <Badge
            label={<span className="dg-overflow">{label}</span>}
            variant={isBridged ? BadgeVariants.blue : BadgeVariants.gray}
            size="sm"
            className="w-full flex justify-center items-center"
        />
    );

    if (!isBridged) {
        return badge;
    }

    return (
        <Tooltip
            placement="top"
            tooltipContent={
                <div className="px-3 py-1.5">
                    <p className="whitespace-nowrap text-xs">{platformName}</p>
                </div>
            }
        >
            {({ setTriggerRef }) => <span ref={setTriggerRef}>{badge}</span>}
        </Tooltip>
    );
}

// // // //

export const NAME_COLUMN: ColumnMetadata<AssetTableRowData, AssetColumns> = {
    label: "Network",
    slug: AssetColumns.name,
    sortsOn: AssetColumns.name,
    type: ColumnTypes.string,
    width: 130,
    sticky: {
        left: 0,
        isLast: true,
    },
    render: (item) => {
        if (item.isAsset) {
            return null;
        }

        const token = item as TokenRowData;
        return (
            <div className="py-0.5 flex items-center">
                <Link
                    variant="default"
                    className="flex items-center gap-2 dg-overflow"
                    href={getNetworkURL({ networkSlug: token.network.slug })}
                    analyticsLabel={AssetUserEvents.TokenTableNetworkLinkClick}
                    analyticsEventProperties={{
                        network: token.network.slug,
                    }}
                >
                    <NetworkBadge
                        network={token.network}
                        enableTooltip={false}
                    />
                    <span className="dg-overflow">{token.network.name}</span>
                </Link>

                <TokenMessages
                    token={token}
                    ticker={token.parentAsset.ticker}
                />
            </div>
        );
    },
};

// // // //

export const TYPE_COLUMN: ColumnMetadata<AssetTableRowData, AssetColumns> = {
    label: "Type",
    slug: "issuance_type",
    type: ColumnTypes.string,
    width: 95,
    render: (item) => {
        if (item.isAsset) {
            return null;
        }

        const token = item as TokenRowData;
        const issuanceType = getTokenIssuanceType(token);

        if (!issuanceType) {
            return <Emdash />;
        }

        return <TokenIssuanceTypeBadge token={token} />;
    },
};

// // // //

const TOKEN_COLUMNS: ColumnMetadata<AssetTableRowData, AssetColumns>[] = [
    { ...NAME_COLUMN, width: 140 }, // NOTE - displays `Network` for tokens
    TYPE_COLUMN,
    TOKENIZATION_TYPE_COLUMN,
    TOKEN_STANDARD_COLUMN,
    TOTAL_SUPPLY_TOKEN_COLUMN,
    CIRCULATING_SUPPLY_TOKEN_COLUMN,
    BRIDGED_TOKEN_SUPPLY_TOKEN_COLUMN,
    HOLDING_ADDRESS_COUNT_COLUMN,
    TRAILING_30_DAY_ACTIVE_ADDRESSES_COUNT_COLUMN,
    TRAILING_30_DAY_TRANSFER_VOLUME_COLUMN,
    TRAILING_30_DAY_TRANSFER_COUNT_COLUMN,
    CONTRACT_ADDRESS_COLUMN,
];

// // // //

export function TokenTable(props: { asset: Asset }) {
    const { asset } = props;

    const { query, setQuery, sortedItems } = useInMemoryAssetSort({
        // NOTE - we need to clone the tokens array to prevent a SSR render bug
        rows: [{ ...asset, tokens: [...asset.tokens] }],
        sort: {
            field: AssetColumns.totalSupplyToken,
            direction: SortDirections.desc,
        },
    });

    return (
        <DataGrid
            query={query}
            columns={TOKEN_COLUMNS}
            rows={sortedItems}
            rowKey="id"
            bordered
            flush
            size={RowSizes.small}
            loading={false}
            analyticsTableName="asset-individual-tokens"
            onChangeQuery={setQuery}
            enableItemsPerPageSelect
        >
            {({ row, scrollLeft }) => (
                <>
                    {(row as Asset).tokens.map((tkn, rowIndex) => (
                        <DataGridRow<AssetTableRowData, AssetColumns>
                            row={{
                                ...tkn,
                                expanded: false,
                                isAsset: false,
                                parentAsset: asset,
                            }}
                            key={tkn.id}
                            columns={TOKEN_COLUMNS}
                            size={RowSizes.small}
                            bordered
                            scrollLeft={scrollLeft}
                            striped={rowIndex % 2 === 1}
                        />
                    ))}
                </>
            )}
        </DataGrid>
    );
}
